import React, { useEffect, useRef } from 'react'
import classnames from 'classnames'
import Cart from './Cart'
import CartIcon from './CartIcon'
import { useHistory } from 'react-router-dom'
import { DeliveryIcon, PickUpIcon } from '@toasttab/buffet-pui-icons'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import { getCartQuantity } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { Channel } from 'src/public/js/siteUtilities'
import { useExperiment } from 'src/shared/components/common/ab_testing/ABTestContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { t } from 'src/translations'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import { useButtonsConfig } from 'src/shared/components/common/button/useButtonsConfig'

export const CART_POPOVER_CONTEXT_KEY = 'cartPopover'

const CartModal = ({
  disabled,
  isOrderPage
}: {
  disabled?: boolean
  isOrderPage?: boolean
}) => {
  const { track } = useTracker()
  const { cart, channel, error, clearError } = useCart()
  const history = useHistory()
  const { ooRestaurant, orderingDisabled, toastProduct } = useRestaurant()
  const { ooReusableFlagGolf: ooCartSitesNavEnabled } = useFlags()
  const { selectedVariant } = useExperiment('woo-cart-in-navigation')
  const cartDisabled =
    (channel !== Channel.ECOMM && orderingDisabled) || !ooRestaurant || disabled
  const numberOfItems = getCartQuantity(cart)
  const context = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const { cartGuid } = useCart()
  const { fulfillmentData } = useFulfillment()

  // The cart-expiry error persists in the long-lived CartContext. Clear it once
  // the guest closes the cart after it's been shown, so the banner appears only
  // once and doesn't re-flash on every subsequent open.
  const expiryShownRef = useRef(false)
  useEffect(() => {
    const isOpen = !!context?.isOpen
    if (isOpen && error?.kind === 'CartError') {
      expiryShownRef.current = true
    } else if (!isOpen && expiryShownRef.current) {
      expiryShownRef.current = false
      clearError()
    }
  }, [context?.isOpen, error, clearError])

  const isEcommPage = toastProduct === ToastProduct.OOEcommerce
  const isCartNavTestEnabled =
    selectedVariant === 'test' && ooCartSitesNavEnabled

  const diningOptionBehavior =
    cart?.diningOptionBehavior ??
    fulfillmentData?.cartFulfillmentData?.diningOptionBehavior

  const trackCartInNavigationClick = () => {
    track(TrackingEvents.CART_IN_NAVIGATION_CLICK, {
      cartGuid: cartGuid,
      isOrderPage,
      variant: selectedVariant,
      numberOfItems
    })
  }

  const onModalClick = () => {
    const shouldNavigateToOrder =
      isCartNavTestEnabled &&
      numberOfItems === 0 &&
      !isEcommPage &&
      !isOrderPage

    trackCartInNavigationClick()

    if (shouldNavigateToOrder) {
      history.push('/order')
    } else {
      context?.open()
    }
  }

  if (!context) {
    return null
  }

  return (
    <>
      <CartModalIconButton
        numberOfItems={numberOfItems}
        isModalOpen={context?.isOpen}
        onClick={onModalClick}
        cartDisabled={cartDisabled}
      />
      <Modal
        isOpen={context.isOpen}
        onClose={context.close}
        className='cartModal'
      >
        <ModalOverlay fadeIn fadeOut />
        <ModalContent
          wrapperClassName='wrapper'
          contentClassName='content'
          slideIn
          slideOut
          ariaLabelledBy='cart-label'
        >
          <div className='header'>
            {diningOptionBehavior ? (
              <div
                className='cartPictureAndLabel'
                data-testid='cart-icon-header'
              >
                {isEcommPage ||
                diningOptionBehavior === DiningOptionBehavior.TakeOut ? (
                  <PickUpIcon className='cartIcon' accessibility='decorative' />
                ) : (
                  <DeliveryIcon
                    className='cartIcon'
                    accessibility='decorative'
                  />
                )}
                <h2 id='cart-label' tabIndex={-1}>
                  {isEcommPage
                    ? t('cart-modal.your-order')
                    : t('cart-modal.your-order-with-type', {
                        orderType:
                          diningOptionBehavior === DiningOptionBehavior.Delivery
                            ? t('cart-modal.delivery')
                            : diningOptionBehavior ===
                              DiningOptionBehavior.TakeOut
                            ? t('cart-modal.pickup')
                            : ''
                      })}
                </h2>
              </div>
            ) : (
              <h2 id='cart-label' tabIndex={-1}>
                {t('cart-modal.your-order')}
              </h2>
            )}
            <ModalCloseButton className='closeButton' />
          </div>
          <Cart />
        </ModalContent>
      </Modal>
    </>
  )
}

export type CartModalIconButtonProps = {
  numberOfItems?: number
  cartDisabled?: boolean
  isModalOpen?: boolean
  onClick?: () => void
}

/**
 * A proxy for the CartModal when in the Editor so we don't have to provide the required Context Providers
 */
export const CartModalIconButton = ({
  numberOfItems,
  cartDisabled,
  isModalOpen,
  onClick
}: CartModalIconButtonProps) => {
  const buttonsConfig = useButtonsConfig()
  return (
    <button
      className='targetAction'
      aria-label={t('cart-modal.cart-with-items', { numberOfItems })}
      {...(isModalOpen && { 'aria-controls': 'cart-control' })}
      {...(!cartDisabled && { 'aria-expanded': isModalOpen })}
      disabled={cartDisabled}
      onClick={onClick}
    >
      <div
        aria-hidden='true'
        className={classnames('cartPopoverTarget', { disabled: cartDisabled })}
        style={{ borderRadius: `${buttonsConfig.borderRadius}px` }}
      >
        <CartIcon />
        <div>{numberOfItems}</div>
      </div>
    </button>
  )
}

export default CartModal
